import { TrashIcon } from "@heroicons/react/24/solid";
import { useState } from "react";
import { generatePath, NavLink } from "react-router-dom";

import BreadcrumbsWrapper from "~/components/BreadcrumbsV2";
import BreadcrumbElement from "~/components/BreadcrumbsV2/components/BreadcrumbElement";
import Button from "~/components/buttons/Button";
import ButtonWithPopoverDots from "~/components/buttons/ButtonWithPopoverDots";
import Card from "~/components/Card";
import CardValueWithLabel from "~/components/CardValueWithLabel";
import CompanyLogo from "~/components/CompanyLogo";
import ContentWrapper from "~/components/ContentWrapper/ContentWrapper.tsx";
import Headline from "~/components/Headline";
import StatusBadge from "~/components/StatusBadge";
import {
	CLIENT_DETAILS_PATH,
	CLIENTS_INDEX_PATH,
	COMPANY_DETAILS_PATH,
	INVOICE_DETAILS_PATH,
	PROJECT_DETAILS_PATH_WITH_TAB_ID,
} from "~/constants/appRoutes.ts";
import {
	getInvoiceStatusDisplayNameById,
	getInvoiceStatusThemeById,
	invoiceStatusId,
} from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import type { Client } from "~/modules/client/api/client/clientTypes.ts";
import type { Company } from "~/modules/client/api/company/companyTypes.ts";
import type { QuotaWithRelations } from "~/modules/client/api/quota/quotaTypes.ts";
import QuotaBudgetStats from "~/modules/client/components/quota/QuotaBudgetStats.tsx";
import AddPhaseToQuotaSidebar
	from "~/modules/client/components/quota/QuotaDetailsView/components/AddPhaseToQuotaSidebar/AddPhaseToQuotaSidebar.tsx";
import DeleteQuotaSidebar
	from "~/modules/client/components/quota/QuotaDetailsView/components/DeleteQuotaSidebar/DeleteQuotaSidebar.tsx";
import QuotaStatusBadge from "~/modules/client/components/quota/QuotaDetailsView/components/QuotaStatusBadge.tsx";
import RemovePhaseFromQuotaModal
	from "~/modules/client/components/quota/QuotaDetailsView/components/RemovePhaseFromQuotaModal/RemovePhaseFromQuotaModal.tsx";
import UpdateQuotaInvoicingDetailsSidebar
	from "~/modules/client/components/quota/QuotaDetailsView/components/UpdateQuotaInvoicingDetailsSidebar/UpdateQuotaInvoicingDetailsSidebar.tsx";
import UpdateQuotaStatusSidebar
	from "~/modules/client/components/quota/QuotaDetailsView/components/UpdateQuotaStatusSidebar/UpdateQuotaStatusSidebar.tsx";
import UpdateQuotaTitleSidebar
	from "~/modules/client/components/quota/QuotaDetailsView/components/UpdateQuotaTitleSidebar/UpdateQuotaTitleSidebar.tsx";
import type { ProjectPhaseWithProject } from "~/modules/project/api/projectPhase/projectPhaseTypes.ts";
import { getFullPhaseTitle } from "~/modules/project/utils/projectPhaseUtils.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

type Props = {
	clientData: Client;
	companyData: Company;
	quotaData: QuotaWithRelations;
};

const QuotaDetailsView = ({ clientData, companyData, quotaData }: Props) => {
	const [showUpdateTitleSidebar, setShowUpdateTitleSidebar] = useState<boolean>(false);
	const [showDeleteQuotaSidebar, setShowDeleteQuotaSidebar] = useState<boolean>(false);
	const [showAddPhaseToQuotaSidebar, setShowAddPhaseToQuotaSidebar] = useState<boolean>(false);
	const [showUpdateStatusSidebar, setShowUpdateStatusSidebar] = useState<boolean>(false);
	const [showUpdateInvoicingDetails, setShowUpdateInvoicingDetails] = useState<boolean>(false);
	const [phaseToRemove, setPhaseToRemove] = useState<ProjectPhaseWithProject | undefined>(undefined);

	const invoiceIsPending = quotaData.invoice.invoiceStatusId === invoiceStatusId.Pending;

	return <div className="auto-grid-2-rows grid h-screen">
		<div className="flex w-full flex-col bg-white">
			<ContentWrapper className="w-full pb-4">
				<BreadcrumbsWrapper>
					<BreadcrumbElement text="Kunden"
									   url={CLIENTS_INDEX_PATH} />
					<BreadcrumbElement text={companyData.displayName}
									   url={generatePath(COMPANY_DETAILS_PATH, { companyId: companyData.id })} />
					<BreadcrumbElement text="Firmen" />
					<BreadcrumbElement text={clientData.displayName}
									   url={generatePath(CLIENT_DETAILS_PATH, { clientId: clientData.id })} />
					<BreadcrumbElement text="Abrechnungskontingente" />
				</BreadcrumbsWrapper>
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-x-3 pt-2">
						{companyData.avatarImage && <CompanyLogo image={companyData.avatarImage}
																 sizeClassName="size-10" />}
						<Headline type="h2">Abrechnungskontingent</Headline>
					</div>
					<Button onClick={()=>setShowDeleteQuotaSidebar(true)} theme="white"><TrashIcon className="size-5 text-danger-500 hover:text-danger-700" /></Button>
				</div>
			</ContentWrapper>
		</div>
		<ContentWrapper className="grid w-full grid-cols-5 gap-x-4 pb-10 pt-4">
			<div className="col-span-3 space-y-4">
				<Card title="Titel / Beschreibung"
					  headerButtonText="Bearbeiten"
					  onHeaderButtonClick={() => setShowUpdateTitleSidebar(true)}>
					{quotaData.title}
				</Card>
				<Card title="Budget">
					<QuotaBudgetStats budgetCentsPlanned={quotaData.budgetCentsPlanned}
									  budgetCentsTracked={quotaData.budgetCentsTracked} />
				</Card>
				<Card title="Enhaltene Phasen"
					  headerButtonText="Phase hinzufügen"
					  onHeaderButtonClick={() => setShowAddPhaseToQuotaSidebar(true)}>
					<div className="mt-2 space-y-4">
						{quotaData.projectPhases.map((phase) => {
							return <div key={phase.id}
										className="flex items-center gap-x-2"><NavLink
								to={generatePath(PROJECT_DETAILS_PATH_WITH_TAB_ID, {
									projectId: phase.projectId,
									tabId: `phase-${phase.phaseNumber}`,
								})}
								className="group flex grow items-center justify-between rounded-lg border border-gray-200  px-4 hover:bg-gray-100">
								<div>
									<Headline type="h5"
											  color="muted">{getFullPhaseTitle(phase)}</Headline>
									<div className="text-sm text-gray-500">{phase.project.titleWithNumber}</div>
								</div>
								<div className="ml-auto" />
								<div>
									<CardValueWithLabel label="Verfügt"
														hasBottomBorder={false}><span className="font-bold text-gray-700">{formatCentsToCurrency(phase.budgetCentsTracked)}</span></CardValueWithLabel>
								</div>


							</NavLink>
								<ButtonWithPopoverDots items={[{
									label: "Phase entfernen",
									onClick: () => setPhaseToRemove(phase),
									icon: TrashIcon,
									iconColorClassName: "fill-danger-500",
								}]}
													   theme="dark" />
							</div>;
						})}
					</div>
				</Card>
			</div>
			<div className="col-span-2 space-y-4">
				<Card title="Status"
					  headerButtonText="Bearbeiten"
					  onHeaderButtonClick={() => setShowUpdateStatusSidebar(true)}
				>
					<QuotaStatusBadge isBookable={quotaData.isBookable} />
				</Card>
				<Card title="Rechnungsstatus"
					  headerButtonText={invoiceIsPending ? "Abrechnungsdetails bearbeiten" : undefined}
					  onHeaderButtonClick={invoiceIsPending ? () => setShowUpdateInvoicingDetails(true) : undefined}
				>
					<div className="flex flex-col gap-y-4">
						<div>
							<StatusBadge theme={getInvoiceStatusThemeById(quotaData.invoice.invoiceStatusId)}>{getInvoiceStatusDisplayNameById(quotaData.invoice.invoiceStatusId)}</StatusBadge>
						</div>
						{!invoiceIsPending &&
							<Button theme="primary"
									to={generatePath(INVOICE_DETAILS_PATH, { invoiceId: quotaData.invoice.id })}>Rechnung
								anzeigen</Button>}
					</div>

				</Card>
			</div>
		</ContentWrapper>
		<UpdateQuotaTitleSidebar isOpen={showUpdateTitleSidebar}
								 onClose={() => setShowUpdateTitleSidebar(false)}
								 quota={quotaData} />

		<DeleteQuotaSidebar isOpen={showDeleteQuotaSidebar}
							onClose={() => setShowDeleteQuotaSidebar(false)}
							quota={quotaData} />

		<AddPhaseToQuotaSidebar isOpen={showAddPhaseToQuotaSidebar}
								onClose={() => setShowAddPhaseToQuotaSidebar(false)}
								quota={quotaData} />

		<RemovePhaseFromQuotaModal clientId={quotaData.id}
								   isLastPhase={quotaData.projectPhases.length === 1}
								   isOpen={!!phaseToRemove}
								   onCloseClick={() => setPhaseToRemove(undefined)}
								   projectPhaseData={phaseToRemove}
								   quotaId={quotaData.id} />

		<UpdateQuotaStatusSidebar isOpen={showUpdateStatusSidebar}
								  onClose={() => setShowUpdateStatusSidebar(false)}
								  quota={quotaData} />

		<UpdateQuotaInvoicingDetailsSidebar isOpen={showUpdateInvoicingDetails}
											onClose={() => setShowUpdateInvoicingDetails(false)}
											quota={quotaData} />
	</div>;
};

export default QuotaDetailsView;